<script setup lang="ts">
type Props = {
  to?: string;
  isButton?: boolean;
  isActive?: boolean;
  target?: string;
  glowColor?: string;
};

const { to, isButton, isActive, target, glowColor } = defineProps<Props>();

const { closeMenu } = useLayoutStore();
const { isLayoutMobileOrTablet, isSidebarMenuOpen } = useLayout();

function onMenuClick() {
  if (isLayoutMobileOrTablet.value) {
    closeMenu();
  }
}

const glowStyle = computed(() =>
  glowColor
    ? ({ '--glow-color': glowColor } as Record<string, string>)
    : undefined,
);
</script>

<template>
  <button
    v-if="isButton"
    :class="[
      $style.menuLink,
      { [$style.isClosed]: !isSidebarMenuOpen },
      { [$style.isActive]: isActive },
      { [$style.hasGlow]: glowColor },
    ]"
    :style="glowStyle"
    @click="onMenuClick"
  >
    <slot />
  </button>

  <NuxtLink
    v-else-if="to"
    :class="[
      $style.menuLink,
      { [$style.isClosed]: !isSidebarMenuOpen },
      { [$style.isActive]: isActive },
      { [$style.hasGlow]: glowColor },
    ]"
    :external="target === '_blank'"
    no-prefetch
    :style="glowStyle"
    :target="target"
    :to="to"
    @click="onMenuClick"
  >
    <slot />
  </NuxtLink>
</template>

<style module lang="scss">
.menuLink {
  @apply flex justify-start items-center text-white text-[14px] leading-normal transition-none font-semibold cursor-pointer relative overflow-hidden;
  background-color: rgba(255, 255, 255, 0.05);
  width: 100%;
  height: 32px;
  padding: 0 10px;
  gap: 9px;
  border-radius: 12px;
  border: 1px solid transparent;

  svg {
    @apply w-[20px] h-[20px] min-w-[20px] relative z-10;
  }

  img,
  [class*='sidebarIcon'] {
    @apply w-[20px] h-[20px] relative z-10;
  }

  span {
    @apply relative z-10 whitespace-nowrap;
  }

  &.isActive {
    border-color: transparent;
  }

  @media (hover: hover) {
    &:hover:not(.isActive) {
      background-color: rgba(255, 255, 255, 0.1);
    }
  }

  /* ENG-800: glow radial azul (laranja no ativo/hover) do menu antigo */
  &.hasGlow {
    border-left: 2.8px solid var(--glow-color, #0606ad);

    &.isActive {
      border-left-color: transparent;
    }
  }

  @media (hover: hover) {
    &.hasGlow:not(.isActive):hover {
      border-left-color: #ff3400;
    }
  }

  &.hasGlow::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(
      circle at -60% 80%,
      var(--glow-color) 0%,
      transparent 60%
    );
    pointer-events: none;
    z-index: 0;
  }

  &.hasGlow.isActive::before {
    --glow-color: #ff3700;
  }

  @media (hover: hover) {
    &.hasGlow:hover::before {
      --glow-color: #ff3700;
    }
  }

  @media screen and (min-width: 1024px) {
    &.isClosed {
      @apply w-[48px] h-[48px] flex justify-center items-center mb-1.5 mx-auto px-0;

      span:not([class*='sidebarIcon']):not([class*='spinner']) {
        @apply hidden;
      }

      svg {
        @apply w-[20px] h-[20px] min-w-[20px];
      }

      img,
      [class*='sidebarIcon'] {
        @apply w-[20px] h-[20px] opacity-100;
      }

      /* Sem o glow no rail o border-left do .hasGlow tambem sai: senao os
         itens de esporte (unicos que passam glow-color fechados) ficam com
         uma faixa azul de 2.8px e o icone fora de centro, e os de cassino
         nao — mesmo rail, aparencia diferente. */
      &.hasGlow {
        border-left: none;
      }

      /* ENG-800: rail retraído + ativo volta ao destaque do menu antigo. O
         legado escreve rgba(247, 169, 105, 1), mas aplica `invert` no tile
         (components/sidebar/MenuItem.vue), entao o que aparece na tela e
         #085696 — o mesmo azul de SportsItems.vue. Sem o invert aqui, o
         valor cru renderiza pessego sobre o rail escuro. */
      &.isActive {
        background-color: #085696;
        border-color: transparent;
      }

      &::before {
        display: none;
      }
    }
  }
}
</style>
